import { useState } from "react";
import { toast } from "sonner";
import { UserRound } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { COUNTRIES } from "./countries";

export function useSave(onSaved) {
  const [busy, setBusy] = useState(false);
  const run = async (call, message) => {
    setBusy(true);
    try {
      const { data } = await call();
      if (onSaved && data?.id) onSaved(data);
      if (message) toast.success(message);
      return true;
    } catch (e) { toast.error(errorText(e)); return false; } finally { setBusy(false); }
  };
  return [busy, run];
}

export function CardTitle({ icon: Icon, children, tone = "" }) {
  return <h2 className="as-title"><span className={`as-icon ${tone}`}><Icon size={16} /></span>{children}</h2>;
}

export function Field({ label, hint, children, ...props }) {
  return (
    <label>{label}{children || <input {...props} />}{hint && <small className="as-hint">{hint}</small>}</label>
  );
}

export function ProfileCard({ data, onSaved }) {
  const [form, setForm] = useState({ username: data.username || "", name: data.name || "", country: data.country || "", email: data.email });
  const [password, setPassword] = useState("");
  const [busy, run] = useSave(onSaved);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const emailChanged = form.email.trim().toLowerCase() !== data.email;

  const submit = async (e) => {
    e.preventDefault();
    const { email, ...profile } = form;
    // Email first: a wrong password then saves nothing.
    if (emailChanged) {
      if (!(await run(() => api.put("/account/email", { email, password })))) return;
      setPassword("");
    }
    await run(() => api.put("/account/profile", profile), emailChanged ? "Profile and email saved" : "Profile saved");
  };

  return (
    <section className="as-card" data-testid="profile-card">
      <CardTitle icon={UserRound}>Personal details</CardTitle>
      <form className="as-fields" onSubmit={submit}>
        <Field label="Username" value={form.username} onChange={set("username")} placeholder="e.g. hoops_fan" maxLength={20}
          hint="3-20 characters: letters, numbers, dot or underscore" data-testid="profile-username-input" />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} required disabled={!data.has_password}
          hint={data.has_password ? null : "Set a password first to change the email of a Google account"} data-testid="email-input" />
        {emailChanged && (
          <Field label="Current password (to change email)" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required autoComplete="current-password" data-testid="email-password-input" />
        )}
        <Field label="Full name" value={form.name} onChange={set("name")} required maxLength={60} data-testid="profile-name-input" />
        <Field label="Country">
          <select value={form.country} onChange={set("country")} data-testid="profile-country-select">
            <option value="">Not set</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </Field>
        <button className="as-primary" disabled={busy} data-testid="profile-save-btn">Save changes</button>
      </form>
    </section>
  );
}
